import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  Box,
  Button,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useGalleryStyle, { GALLERY_CORNER_RADIUS } from "./ContentGallery.styles";
import {
  DEFAULT_GALLERY_SORT,
  GALLERY_DATE_TYPES,
  GalleryDateType,
  GallerySortBy,
  GallerySortOrder,
  GallerySortValue,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC,
} from "./utils/gallerySortFilter";

type GallerySortModalProps = {
  open: boolean;
  onClose: () => void;
  value: GallerySortValue | null;
  // eslint-disable-next-line no-unused-vars
  onApply: (next: GallerySortValue) => void;
  onClear: () => void;
};

const DATE_FIELD_OPTIONS: { value: GalleryDateType; labelKey: string }[] = [
  { value: GALLERY_DATE_TYPES.PUBLISHED, labelKey: "published_date" },
  { value: GALLERY_DATE_TYPES.CREATED, labelKey: "created_date" },
  { value: GALLERY_DATE_TYPES.MODIFIED, labelKey: "last_modified" },
];

type OrderOption = { value: GallerySortOrder; labelKey: string; testId: string };

const ORDER_OPTIONS: OrderOption[] = [
  { value: SORT_ORDER_DESC, labelKey: "latest_first", testId: "gallery-sort-order-desc" },
  { value: SORT_ORDER_ASC, labelKey: "oldest_first", testId: "gallery-sort-order-asc" },
];

const GallerySortModal = ({ open, onClose, value, onApply, onClear }: GallerySortModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useGalleryStyle();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [sortBy, setSortBy] = useState<GallerySortBy>(DEFAULT_GALLERY_SORT.sortBy);
  const [dateType, setDateType] = useState<GalleryDateType>(DEFAULT_GALLERY_SORT.dateType);
  const [order, setOrder] = useState<GallerySortOrder>(DEFAULT_GALLERY_SORT.order);

  useEffect(() => {
    if (!open) return;
    const seed = value || DEFAULT_GALLERY_SORT;
    setSortBy(seed.sortBy);
    setDateType(seed.dateType);
    setOrder(seed.order);
  }, [open, value?.sortBy, value?.dateType, value?.order]);

  const fullBleedDivider = { margin: "0 -20px", borderColor: "#EFF0F6" };

  const handleApply = () => {
    onApply({ sortBy, dateType, order });
    onClose();
  };

  const handleClearAll = () => {
    setSortBy(DEFAULT_GALLERY_SORT.sortBy);
    setDateType(DEFAULT_GALLERY_SORT.dateType);
    setOrder(DEFAULT_GALLERY_SORT.order);
    onClear();
    onClose();
  };

  const renderSortByCard = (
    cardValue: GallerySortBy,
    icon: React.ReactNode,
    title: string,
    caption: string,
    testId: string,
  ) => {
    const isSelected = sortBy === cardValue;
    return (
      <Box
        className={`${classes.sortByCard} ${isSelected ? "selected" : ""}`}
        role='radio'
        aria-checked={isSelected}
        tabIndex={0}
        data-testid={testId}
        onClick={() => setSortBy(cardValue)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setSortBy(cardValue);
          }
        }}>
        {icon}
        <Typography variant='p3medium' className='sortByCardTitle'>
          {title}
        </Typography>
        <Typography variant='p4regular' className='sortByCardCaption'>
          {caption}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='gallery-sort-dialog-title'
      sx={{
        ".Platform-x-Dialog-paper": {
          borderRadius: GALLERY_CORNER_RADIUS,
          padding: "20px",
          margin: { xs: "16px" },
          width: { xs: "100%", sm: "420px" },
          maxWidth: { xs: "calc(100% - 32px)", sm: "420px" },
        },
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: "12px",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SwapVertIcon sx={{ fontSize: "20px", color: "#14142B" }} />
          <Typography variant='h5bold' id='gallery-sort-dialog-title'>
            {t("sort")}
          </Typography>
        </Box>
        <IconButton
          aria-label={t("close")}
          data-testid='gallery-sort-close'
          onClick={onClose}
          size='small'
          sx={{ color: "#8991A5" }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      <Divider sx={fullBleedDivider} />

      <Typography variant='h6bold' sx={{ margin: "20px 0 10px" }}>
        {t("sort_by")}
      </Typography>
      <Box
        role='radiogroup'
        aria-label={t("sort_by")}
        sx={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
        {renderSortByCard(
          "date",
          <CalendarTodayOutlinedIcon sx={{ fontSize: "18px" }} />,
          t("sort_date"),
          t("order_by_date"),
          "gallery-sort-by-date",
        )}
      </Box>

      <Divider sx={fullBleedDivider} />

      <Typography variant='h6bold' sx={{ margin: "20px 0 10px", color: "#14142B" }}>
        {t("date_field")}
      </Typography>
      <RadioGroup
        value={dateType}
        onChange={(event) => setDateType(event.target.value as GalleryDateType)}
        className={classes.sortOptionGrid}
        aria-label={t("date_field")}>
        {DATE_FIELD_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            data-testid={`gallery-sort-datefield-${option.value}`}
            control={<Radio size='small' />}
            label={<Typography variant='p4regular'>{t(option.labelKey)}</Typography>}
          />
        ))}
      </RadioGroup>

      <Divider sx={fullBleedDivider} />

      <Typography variant='h6bold' sx={{ margin: "20px 0 10px" }}>
        {t("order")}
      </Typography>
      <RadioGroup
        value={order}
        onChange={(event) => setOrder(event.target.value as GallerySortOrder)}
        className={classes.sortOptionGrid}
        aria-label={t("order")}>
        {ORDER_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            data-testid={option.testId}
            control={<Radio size='small' />}
            label={<Typography variant='p4regular'>{t(option.labelKey)}</Typography>}
          />
        ))}
      </RadioGroup>

      <Divider sx={{ ...fullBleedDivider, marginTop: "24px" }} />

      <Box
        sx={{
          display: "flex",
          gap: "16px",
          marginTop: "20px",
          flexDirection: isMobile ? "column" : "row",
        }}>
        <Button
          variant='secondaryButton'
          fullWidth
          data-testid='gallery-sort-clear'
          onClick={handleClearAll}>
          {t("clear_all")}
        </Button>
        <Button
          variant='primaryButton'
          fullWidth
          data-testid='gallery-sort-apply'
          onClick={handleApply}>
          {t("apply")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default GallerySortModal;
